import React, {useCallback, useEffect, useState} from 'react';
import {Trans} from '@lingui/macro';
import ChatBubble from '@adsk/ha-chat-bubble';
import PropTypes from 'prop-types';
import {HelpAnywhereCloseBtnIcon} from '@apothecary/community-icons';
import './_PopupText.scss';
import {adpAnalyticsLog, buildAdpAnalyticsPayload, isStorageAccessible} from '../../utils';
import {default as ButtonDhig} from '@digital-hig/button';
import ThemeMui from '@digital-hig/theme-mui';

const isLocalStorageAvailable = isStorageAccessible(() => localStorage);
// show user a helpful message above UHM
const PopupText = (props) => {
  const chatBubbleStyles = {
    boxSizing: 'border-box',
    marginBottom: '30px',
    marginRight: '30px',
    height: 'auto',
    maxWidth: '263px',
  };

  const {
    title,
    text,
    beta,
    delaySeconds = 0,
    dismissSeconds = 0,
    intervalDays = 0,
    onOpen,
    onClose,
    launchDarklyFlags,
    primaryButtonText,
    onPrimaryClick,
    secondaryButtonText,
    onSecondaryClick,
    minDays = 0,
    maxDays = 10000,
    popupType = 'welcome',
  } = props;
  const [visible, setVisible] = useState(false);
  const [timeSeen, setTimeSeen] = useState(null);
  const [windowIsClosing, setWindowIsClosing] = useState(false);

  const {tempAnimatePopup} = launchDarklyFlags;

  const handleClose = useCallback(() => {
    const timeout = tempAnimatePopup ? 200 : 0;
    setWindowIsClosing(true);
    setTimeout(() => {
      setWindowIsClosing(false);
      setVisible(false);
      if (typeof onClose === 'function') onClose();
    }, timeout);
  }, [onClose, tempAnimatePopup]);

  const hidePopup = useCallback(() => {
    const timeToDismiss = dismissSeconds * 1000;
    const now = Date.now();

    // if user clicked 'X' on the popup *before* it automatically
    // got removed from the screen, we want to send ADP event below
    const popUpNotDismissed = now - timeSeen < timeToDismiss;
    if (popUpNotDismissed) {
      adpAnalyticsLog(
        buildAdpAnalyticsPayload({
          e: 'uhm_dismiss_popup',
          ev: true,
        })
      );
    }

    if (typeof onClose === 'function') handleClose();
  }, [onClose, dismissSeconds, timeSeen, handleClose]);

  const showPopup = useCallback(() => {
    let shouldShowPopup = false;
    const now = Date.now();
    if (isLocalStorageAvailable) {
      const hasSeenPopup = window.localStorage?.getItem('popupSeen');
      let numPopupsSeen = window.localStorage.getItem('numPopupsSeen');
      if (!numPopupsSeen) {
        numPopupsSeen = 0;
      }
      // only show popup inbetween a range of days defined in LD configs
      if (numPopupsSeen >= minDays && numPopupsSeen <= maxDays) {
        if (intervalDays && hasSeenPopup) {
          const seenDate = new Date(parseInt(hasSeenPopup, 10) ?? now);
          const showDate = new Date(seenDate.valueOf());
          showDate.setDate(seenDate.getDate() + intervalDays);
          showDate.setHours(0, 0, 0, 0);
          shouldShowPopup = showDate.getTime() <= now;
        } else {
          shouldShowPopup = !hasSeenPopup;
        }

        if (shouldShowPopup) {
          if (typeof onOpen === 'function') onOpen();
          setVisible(true);
          adpAnalyticsLog(
            buildAdpAnalyticsPayload({
              e: 'pop-up_shown',
              ev: popupType,
            })
          );
          if (dismissSeconds) setTimeout(hidePopup, dismissSeconds * 1000);
          window.localStorage?.setItem('popupSeen', now.toString());
          const numPopupsSeen = window.localStorage.getItem('numPopupsSeen');
          window.localStorage?.setItem(
            'numPopupsSeen',
            numPopupsSeen ? parseInt(numPopupsSeen) + 1 : 1
          );
        }

        // migrate legacy boolean value
        if (!parseInt(hasSeenPopup, 10))
          window.localStorage?.setItem('popupSeen', now.toString());
      }
    }
  }, []);

  useEffect(() => {
    let showTimer = null;

    // check if flags are not necessary for Popup (e.g. for dotCom)
    // or if LD flags returned before starting timer (e.g. ACD)
    const flagStatus = launchDarklyFlags?.permanentShouldShowUa;
    if (flagStatus) {
      showTimer = setTimeout(showPopup, delaySeconds * 1000);
    } else return;

    return () => clearTimeout(showTimer);
  }, [delaySeconds, showPopup, launchDarklyFlags]);

  useEffect(() => {
    // set & use this variable to determine if we need to send ADP `uhm_dismiss_popup` event
    setTimeSeen(Date.now());
  }, []);

  const message = (
    <>
      {beta && (
        <p className="beta-chip float-override">
          <Trans>Beta</Trans>
        </p>
      )}
      <p className="fab-popup__heading">{title}</p>
      <button
        onClick={() => {
          adpAnalyticsLog(
            buildAdpAnalyticsPayload({
              e: 'pop-up_dismissed',
              ev: `x_${popupType}`,
            })
          );
          handleClose()
        }}
        className="fab-popup__button"
        aria-label="Close"
      >
        <HelpAnywhereCloseBtnIcon color="#fff" height="24px" width="24px" />
      </button>
      <p className="fab-popup__message">{text}</p>
      {primaryButtonText && (
        <div className="fab-popup__button__container">
          <ButtonDhig
            onClick={() => {
              adpAnalyticsLog(
                buildAdpAnalyticsPayload({
                  e: 'uhm_icon_click',
                  ev: 'pop-up',
                })
              );
              onPrimaryClick()}}
            style={{
              fontSize: '12px',
              lineHeight: '18px',
            }}
            variant="outlined"
            aria-label="Primary"
          >
            {primaryButtonText}
          </ButtonDhig>
          {secondaryButtonText && (
            <ButtonDhig
              onClick={() => {
                adpAnalyticsLog(
                  buildAdpAnalyticsPayload({
                    e: 'pop-up_dismissed',
                    ev: 'no_thanks',
                  })
                );
                handleClose()
              }}
              aria-label="Secondary"
              style={{
                fontSize: '12px',
                lineHeight: '18px',
              }}
              variant="text"
            >
              {secondaryButtonText}
            </ButtonDhig>
          )}
        </div>
      )}
    </>
  );

  return visible ? (
    <ThemeMui baseThemeName="dhig2021Dark">
      <div
        className={`fab-popup ${
          tempAnimatePopup
            ? windowIsClosing
              ? `popup-fade-out`
              : `popup-fade-in`
            : ''
        }`}
      >
        <ChatBubble styles={chatBubbleStyles}>{message}</ChatBubble>
      </div>
    </ThemeMui>
  ) : null;
};

PopupText.propTypes = {
  title: PropTypes.element,
  text: PropTypes.element,
  beta: PropTypes.bool,
  delaySeconds: PropTypes.number,
  dismissSeconds: PropTypes.number,
  intervalDays: PropTypes.number,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  primaryButtonText: PropTypes.element,
  onPrimaryClick: PropTypes.func,
  secondaryButtonText: PropTypes.element,
  onSecondaryClick: PropTypes.func,
};

PopupText.defaultProps = {
  title: 'Get help',
  text: 'Quickly find answers to questions or connect with an expert',
  beta: false,
  delaySeconds: 0,
  dismissSeconds: 0,
  intervalDays: 0,
};

export default PopupText;
